import React, { Component } from "react";
import i18n from "i18next";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches any runtime errors in the subtree and renders a graceful fallback
 * instead of crashing the entire application.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary] Uncaught error:", error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    const { t } = i18n;

    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex flex-col items-center justify-center gap-4 p-8 rounded-2xl glass-panel text-center max-w-md mx-auto mt-10">
          <div className="text-3xl">⚠️</div>
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
            {t("common.error_boundary.title", "Midagi läks valesti")}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t("common.error_boundary.message", "Rakenduses tekkis ootamatu viga. Proovige lehte värskendada.")}
          </p>
          <button
            onClick={this.handleReset}
            className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
          >
            {t("common.error_boundary.retry", "Proovi uuesti")}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
